import React, { useEffect, useState, useMemo } from 'react';
import { Form } from 'react-bootstrap';
import { connect } from 'react-redux';
import { getFormSyncErrors, getFormValues, isValid, reduxForm } from 'redux-form';
import { get } from 'lodash';
import getIncludedResource from '../../../../helpers/getIncludedResource';
import './SmartLuggageForm.scss';
import { REVIEW_TAB, PAY_TAB } from './tabs';
import { isBroker } from '../../../auth/redux/authActions';
import { compose } from 'redux';
import Sidebar from './components/Sidebar/Sidebar';
import QuoteTabsContainer from './components/QuoteTabsContainer/QuoteTabsContainer';
import scrollToTop from '../../../../helpers/scrollToTop';

const FORM_NAME = 'SmartLuggageForm';

const hasPremiumGotErrors = (selectedPremium) => {
  const declines = selectedPremium?.declines ?? [];
  const errors = selectedPremium?.errors ?? [];
  const allErrors = declines.concat(errors);
  return !!allErrors.length;
};

const SmartLuggageForm = ({
  calculatedPremiums,
  channelType,
  definitions,
  productName,
  productCode,
  productRules,
  purchaseDeclaration,
  source,
  isCalculating,
  isSaving,
  isMta,
  onSave,
  onCalculate,
  onSaveAndExit,
  onSaveEmailAndExit,
  onRedirectToIssued,
  onIssue,
  change,
  dispatch,
  formValues,
  formErrors,
  handleSubmit,
  submitting,
  submitFailed,
}) => {
  const [activeTab, setActiveTab] = useState(1);
  const [lastVisitedTab, setLastVisitedTab] = useState(1);
  const [finalPremium, setFinalPremium] = useState(null);
  const sourceId = formValues?.data.relationships.source.data.id;
  const paymentType = formValues?.meta?.payment_type;
  const schemeId = formValues?.data.relationships.schemes?.data?.[0]?.id;
  const selectedPremium = finalPremium
    ? finalPremium.attributes
    : calculatedPremiums?.find((premium) => premium.meta.scheme.id === schemeId)?.meta;
  const declines = selectedPremium?.declines ?? [];
  const errors = selectedPremium?.errors ?? [];
  const allErrors = declines.concat(errors);
  const campaignCode = formValues?.data.attributes.metadata.campaign;
  const startDate = formValues?.data.attributes.metadata.start_date;
  const endDate = formValues?.data.attributes.metadata.end_date;
  const isPurchaseDeclarationAgreed = formValues?.meta?.purchase_declaration;
  const isRefund = formValues?.meta?.refund;

  const updateSource = useMemo(
    () => (newSource) => {
      if (!isBroker() && !!newSource.relationships) {
        dispatch(change('data.relationships.source.data.id', newSource.id));
        dispatch(
          change(
            'data.relationships.channel.data.id',
            get(newSource, 'relationships.channel.data.id', ''),
          ),
        );
        if (newSource.attributes.is_broker) {
          const isBrokerChannel =
            !newSource.attributes.is_channel &&
            newSource.attributes.broker_type === 'broker';
          // const isBrokerChannel =  !channelId && brokerId  &&  isBrokerType;
          dispatch(
            change(
              'data.relationships.broker.data.id',
              newSource.relationships.broker.data.id,
            ),
          );

          if (isBrokerChannel) {
            dispatch(change('data.attributes.metadata.sales_channel', 'jtc-b2b'));
          }
        } else {
          dispatch(change('data.relationships.broker.data.id', ''));
          dispatch(change('data.attributes.metadata.sales_channel', ''));
        }
      }
    },
    [dispatch, change],
  );
  const numTravellers = get(formValues, 'data.attributes.metadata.num_travellers');

  useEffect(
    () => {
      if (source && source.id !== sourceId) {
        updateSource(source);
      }
    },
    [updateSource, source, sourceId],
  );

  useEffect(
    () => {
      if (numTravellers !== undefined) {
        onCalculate(formValues);
      }
    },
    [numTravellers],
  );

  useEffect(
    () => {
      if (startDate) {
        onCalculate(formValues);
      }
    },
    [startDate],
  );

  useEffect(
    () => {
      if (campaignCode || campaignCode === '') {
        calculateQuotes();
      }
    },
    [campaignCode],
  );

  useEffect(
    () => {
      const gross = finalPremium?.attributes?.gross;
      if (isMta && gross < 0 && isRefund === undefined) {
        dispatch(change('meta.refund', true));
      }
    },
    [isMta, finalPremium],
  );

  useEffect(
    () => {
      if (endDate) {
        if (new Date(startDate) > new Date(endDate)) {
          dispatch(change('data.attributes.metadata.end_date_before_start_date', true));
        } else {
          dispatch(change('data.attributes.metadata.end_date_before_start_date', false));
        }
      }
    },
    [startDate, endDate],
  );

  const calculateQuotes = () => {
    dispatch(handleSubmit(onCalculate));
  };

  const saveApplicationAndGoToPayment = (values) => {
    const selectedSchemes = values.data.relationships.schemes.data;
    if (selectedSchemes.length === 1) {
      return Promise.resolve(onSave(values, true)).then((resp) => {
        const premiums = getIncludedResource(resp.data, resp.included, 'premiums');
        // if (resp.data.attributes.valid) {
        dispatch(change('data.id', resp.data.id));
        if (premiums && premiums.length === 1) {
          const premium = premiums[0];
          setFinalPremium(premium);
          const hasErrors = hasPremiumGotErrors(premium?.attributes);
          if (hasErrors) {
            scrollToTop();
            return;
          }

          dispatch(change('data.relationships.premium.data.id', premium.id));
          dispatch(change('meta.amount', premium.attributes.gross));
          setActiveTab(PAY_TAB);
        }
      });
    }
    return null;
  };

  const calculateAndGoToDetailsTab = () => {
    return Promise.resolve(onCalculate(formValues)).then((resp) => {
      const selectedPremium = resp?.find(
        (premium) => premium.meta.scheme.id === schemeId,
      );
      const hasErrors = hasPremiumGotErrors(selectedPremium?.meta);

      if (hasErrors) {
        scrollToTop();
        return;
      }
      setActiveTab(REVIEW_TAB);
    });
  };

  const handlePaymentTab = () => {
    return dispatch(handleSubmit(saveApplicationAndGoToPayment));
  };
  const handleDetailsTab = () => {
    return dispatch(handleSubmit(calculateAndGoToDetailsTab));
  };

  const handleTab = (key) => {
    const lastVisited = key > lastVisitedTab ? key : lastVisitedTab;

    const isBack = key < activeTab;
    if (key === PAY_TAB) {
      setLastVisitedTab(lastVisited);
      return handlePaymentTab();
    }
    // remove final premium for all tabs that are not the payment tab
    setFinalPremium(null);
    if (key === REVIEW_TAB && !isBack) {
      setLastVisitedTab(lastVisited);
      return handleDetailsTab();
    }
    setLastVisitedTab(lastVisited);
    dispatch(change('data.current_tab', key));
    setActiveTab(key);
  };

  const handleNextTab = () => {
    return handleTab(activeTab + 1);
  };

  const handlePreviousTab = () => {
    handleTab(activeTab - 1);
  };

  const validateAndGoToNextTab = () => {
    return dispatch(handleSubmit(handleNextTab));
  };

  const handleIssuePolicy = () => {
    dispatch(handleSubmit(onIssue));
  };

  const navigateToTab = (key) => {
    //tab menu navigation
    if (key <= lastVisitedTab && key !== PAY_TAB) {
      // skip validation when navigating already visited tabs through tab nav
      setActiveTab(key);
      return;
    }
    handleTab(key);
  };

  const handleCommissionAdjustments = () => {
    calculateQuotes();
  };

  const handleSaveAndExit = () => {
    onSaveAndExit(formValues);
  };

  const handleEmailSaveAndExit = () => {
    onSaveEmailAndExit(formValues);
  };

  return (
    <div className={'smart-luggage-form'}>
      <Form horizontal>
        <QuoteTabsContainer
          activeTab={activeTab}
          allErrors={allErrors}
          calculatedPremiums={calculatedPremiums}
          channelType={channelType}
          definitions={definitions}
          finalPremium={finalPremium}
          numTravellers={numTravellers}
          paymentDeclarations={formValues?.data.attributes.metadata.payment_declarations}
          paymentType={paymentType}
          productCode={productCode}
          productRules={productRules}
          purchaseDeclaration={purchaseDeclaration}
          salesChannel={formValues?.data.attributes.metadata.sales_channel}
          selectedSchemes={formValues?.data.relationships.schemes.data}
          startDate={startDate}
          isMta={isMta}
          isSaving={isCalculating || submitting}
          isCalculating={isCalculating}
          isPurchaseDeclarationAgreed={isPurchaseDeclarationAgreed}
          isRefund={isRefund}
          onTabChange={navigateToTab}
          onNextTab={validateAndGoToNextTab}
          onPreviousTab={handlePreviousTab}
          onIssuePolicy={handleIssuePolicy}
          onRedirectToIssued={onRedirectToIssued}
        />

        <Sidebar
          application={formValues}
          productName={productName}
          isMta={isMta}
          formErrors={formErrors}
          submitFailed={submitFailed}
          source={source}
          calculatedPremiums={calculatedPremiums || []}
          selectedSchemes={formValues?.data.relationships.schemes.data}
          showSave={activeTab > REVIEW_TAB}
          showNext={activeTab < PAY_TAB}
          goToNextPage={validateAndGoToNextTab}
          isSubmitting={submitting}
          isSaving={isSaving}
          handleCommissionAdjustments={handleCommissionAdjustments}
          calculateQuotes={calculateQuotes}
          saveAndExit={handleSaveAndExit}
          saveEmailAndExit={handleEmailSaveAndExit}
        />
      </Form>
    </div>
  );
};

export default compose(
  connect(
    (state, ownProps) => {
      return {
        initialValues: ownProps.resource,
        formValues: getFormValues(FORM_NAME)(state),
        formErrors: getFormSyncErrors(FORM_NAME)(state),
        valid: isValid(FORM_NAME)(state),
        isCalculating: state.product.isCalculating,
      };
    },
    null,
  ),
  reduxForm({ form: FORM_NAME }),
)(SmartLuggageForm);
